<template>
  <div class="q-gutter-y-lg">
    <div class="row text-subtitle1">
      <div class="col-3">
        <strong>Client: </strong>
        {{ $filters.clientName(postSearch.client) }}
      </div>
      <div class="col-6 text-center">
        <strong>Keywords: </strong>
        {{ postSearch.keywords.join(', ') }}
      </div>
      <div class="col-3 text-right">
        <strong>Total Matches:</strong> {{ postSearch.numPostsMatched }}
      </div>
    </div>
    <posts
      :fetch="loadPosts"
      :filters="filters"
      infinite-scroll
      scroll-target=".custom-scrollbar"
      persist
    >
      <template v-slot="{ visiblePosts }">
        <posts-grid :posts="visiblePosts">
          <template v-slot="post">
            <post
              :post="post"
              :highlight="getHighlightRules(post)"
              show-client
              notes
              trash
              actions
              scrollable
            />
          </template>
        </posts-grid>
      </template>
    </posts>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {
  Post as PostType,
  PostFilters,
  PostSearch,
  PostSearchMatch,
  PostSearchMatchFilters,
  QueryResult,
} from '@/type';
import Posts from '@/social/post/list/Posts.vue';
import { status } from '@/social/post/post';
import { name as clientName } from '@/client/client-filter';
import matchRepository from './post-search-match-repository';
import Post from '../card/Post.vue';
import PostsGrid from '../list/PostsGrid.vue';
import { PostSearchMatchRule, PostSearchMatchRuleType } from './types';

interface Data {
  matches: PostSearchMatch[],
  filters: PostFilters,
}

export default defineComponent({
  meta() {
    return {
      title: 'Search Results',
    };
  },
  components: { PostsGrid, Posts, Post },
  props: {
    postSearch: {
      type: Object as () => PostSearch,
      required: true,
    },
  },
  data(): Data {
    return {
      matches: [],
      filters: {
        statuses: [
          status.UNREVIEWED,
          status.SAVED,
          status.ESCALATED,
          status.RESOLVED,
        ],
        socialSiteIds: [],
        isTrashed: false,
        since: null,
        until: null,
        order: 'desc',
      },
    };
  },
  methods: {
    loadMatches(filters: PostSearchMatchFilters): Promise<QueryResult<PostSearchMatch>> {
      return matchRepository
        .findAll(this.postSearch.id, filters)
        .then((result) => {
          // Prev matches should not be overwritten,
          // needed to get the highlight rules.
          this.matches = this.matches.concat(result.items);
          return result;
        });
    },
    loadPosts(filters: PostFilters): Promise<QueryResult<PostType>> {
      return this.loadMatches(filters).then((result) => {
        const posts = result.items.map(psm => psm.post);
        return {
          total: result.total,
          items: posts,
        };
      });
    },
    getHighlightRules(post: PostType): PostSearchMatchRule[] {
      const matches = this.matches.filter((m: PostSearchMatch) => m.post.id === post.id);

      if (matches.length === 0) return [];

      return matches.flatMap((match: PostSearchMatch) => match.keywords.map(keyword => ({
        operation: 'contains',
        needle: keyword,
        type: (match.type as any) as PostSearchMatchRuleType,
      })));
    },
  },
  filters: {
    clientName,
  },
});
</script>
